// Sprint 2026-Jun — VoltLedger integration Phase F
// Receipt Voucher Register — list/filter grouping vouchers, drill down to the
// underlying standard Payment Receipts, metadata edit, and full reversal
// (reverses child receipts: SI received_amount + ledger). Reversed vouchers
// stay on the register as an audit trail.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import { BranchFilter } from "@/components/BranchFilter";
import { AttachmentChip } from "@/components/AttachmentsField";
import { PaginationControls } from "@/components/PaginationControls";
import { toast, Toaster } from "sonner";
import {
  Loader2, Pencil, RotateCcw, RotateCw, Search, X,
} from "lucide-react";

const MODES = ["Bank", "Cash", "UPI", "Cheque"];
const selCls = "border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";
const EMPTY = { q: "", branch_id: "", client_id: "", mode: "", status: "", date_from: "", date_to: "" };
const LIMIT = 25;

export default function ReceiptVoucherRegister() {
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(LIMIT);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null); // voucher detail

  useEffect(() => { api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {}); }, []);

  async function load(f = filters, p = page, ps = pageSize) {
    setLoading(true);
    try {
      const params = { skip: p * ps, limit: ps };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const r = await api.get("/receipt-vouchers", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load register");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const apply = () => { setPage(0); load(filters, 0); };

  async function openDetail(id) {
    try {
      const r = await api.get(`/receipt-vouchers/${id}`);
      setSelected(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load voucher"); }
  }

  return (
    <div data-testid="receipt-voucher-register-page">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Receivables"
        title="Receipt Voucher Register"
        subtitle="Grouping and tracking register for bulk receipts. The money itself lives in standard Payment Receipts — drill into any voucher to see them."
      />
      <Card className="p-3 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2 items-end">
          <div className="col-span-2 relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="rvr-search" className={`${selCls} w-full pl-8`} placeholder="Voucher / invoice no / client / ref…"
              value={filters.q} onChange={(e) => set("q", e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && apply()} />
          </div>
          <BranchFilter value={filters.branch_id} onChange={(v) => set("branch_id", v)} testid="rvr-branch" />
          <select data-testid="rvr-client" className={selCls} value={filters.client_id} onChange={(e) => set("client_id", e.target.value)}>
            <option value="">All Clients</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="rvr-mode" className={selCls} value={filters.mode} onChange={(e) => set("mode", e.target.value)}>
            <option value="">All Modes</option>
            {MODES.map((m) => <option key={m}>{m}</option>)}
          </select>
          <select data-testid="rvr-status" className={selCls} value={filters.status} onChange={(e) => set("status", e.target.value)}>
            <option value="">All Status</option>
            <option>Posted</option>
            <option>Reversed</option>
          </select>
          <input data-testid="rvr-date-from" type="date" className={selCls} value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} />
          <div className="flex gap-1.5">
            <input data-testid="rvr-date-to" type="date" className={`${selCls} flex-1`} value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} />
            <button data-testid="rvr-apply" onClick={apply}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-2 text-xs font-bold">
              <RotateCw size={13} className={loading ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </Card>

      <Card>
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <div data-testid="rvr-summary">
            {data?.total ?? 0} voucher(s) · Posted total <span className="font-bold text-slate-800">{formatINR(data?.posted_amount || 0)}</span>
            {data?.posted_tds > 0 && <> · TDS {formatINR(data.posted_tds)}</>}
            {data?.posted_cn > 0 && <> · CN allocated {formatINR(data.posted_cn)}</>}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="rvr-table">
            <thead>
              <tr className="bg-slate-100 text-slate-600">
                {["Voucher No", "Receipt Date", "Entry Date", "Client", "Branch", "Mode", "Reference", "Invoices", "Amount", "TDS", "CN Alloc", "Ret Held", "Ret Released", "Source", "Status", "Created By"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider whitespace-nowrap">{h}</th>))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={16} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
              {!loading && !(data?.rows || []).length && (
                <tr><td colSpan={16} className="px-3 py-8 text-center text-slate-400">No receipt vouchers found.</td></tr>)}
              {!loading && (data?.rows || []).map((v) => (
                <tr key={v.id} data-testid={`rvr-row-${v.voucher_number}`}
                  className="border-b border-slate-50 hover:bg-orange-50/40 cursor-pointer"
                  onClick={() => openDetail(v.id)}>
                  <td className="px-3 py-2 font-bold whitespace-nowrap text-orange-700">{v.voucher_number}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{v.voucher_date}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-500" title={(v.created_at || "").slice(0, 16).replace("T", " ")}
                    data-testid={`rvr-entry-date-${v.voucher_number}`}>{(v.created_at || "").slice(0, 10) || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-semibold">{v.client_name}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{v.branch_name || "—"}</td>
                  <td className="px-3 py-2">{v.mode}</td>
                  <td className="px-3 py-2 whitespace-nowrap max-w-[120px] truncate" title={v.reference_no}>{v.reference_no || "—"}</td>
                  <td className="px-3 py-2 text-center">{v.allocation_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold">{formatINR(v.total_amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{v.tds_total ? formatINR(v.tds_total) : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-violet-700">{v.cn_allocation_total ? formatINR(v.cn_allocation_total) : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{v.retention_held_total ? formatINR(v.retention_held_total) : "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700">{v.retention_released_total ? formatINR(v.retention_released_total) : "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{v.source === "excel_upload" ? "Excel" : "Manual"}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase border ${v.status === "Reversed" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
                      {v.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-500">{v.created_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-slate-100">
          <PaginationControls page={page + 1} pageSize={pageSize} total={data?.total || 0} loading={loading}
            onPage={(n) => { const p = Math.max(0, n - 1); setPage(p); load(filters, p); }}
            onPageSize={(n) => { const ps = Number(n) || LIMIT; setPageSize(ps); setPage(0); load(filters, 0, ps); }}
            pageSizeOptions={[25, 50, 100, 200]} />
        </div>
      </Card>

      {selected && (
        <VoucherDrawer voucher={selected} onClose={() => setSelected(null)}
          onChanged={() => { setSelected(null); load(); }} />
      )}
    </div>
  );
}

function VoucherDrawer({ voucher: v, onClose, onChanged }) {
  const navigate = useNavigate();
  const [reverseOpen, setReverseOpen] = useState(false);
  const editBlocked = (v.cn_allocations || []).length > 0 || (v.adjustment_effects || []).length > 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div data-testid="rvr-detail-drawer" onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-3xl h-full overflow-y-auto shadow-2xl border-l border-slate-200">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-orange-600">Receipt Voucher</div>
            <div className="text-base font-bold text-slate-900">{v.voucher_number}
              <span className={`ml-2 px-1.5 py-0.5 text-[10px] font-bold uppercase border align-middle ${v.status === "Reversed" ? "border-rose-200 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>{v.status}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {v.status === "Posted" && (
              <>
                <button data-testid="rvr-edit-btn"
                  onClick={() => editBlocked
                    ? toast.error("This voucher carries Credit Note allocations or CESS/LD/Round-off adjustments — reverse it and create a new one (audit-safe)")
                    : navigate(`/bulk-receipts?edit=${v.id}`)}
                  title={editBlocked ? "Not editable — carries CN allocations / adjustments. Reverse & recreate." : "Open the voucher page to edit entries"}
                  className={`flex items-center gap-1.5 border px-3 py-2 text-xs uppercase tracking-wider font-bold ${editBlocked ? "border-slate-200 text-slate-400 cursor-not-allowed" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
                  <Pencil size={12} /> Edit
                </button>
                <button data-testid="rvr-reverse-btn" onClick={() => setReverseOpen(true)}
                  className="flex items-center gap-1.5 border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 px-3 py-2 text-xs uppercase tracking-wider font-bold">
                  <RotateCcw size={12} /> Reverse
                </button>
              </>
            )}
            <button data-testid="rvr-drawer-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
        </div>

        <div className="p-5 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
          {[["Date", v.voucher_date], ["Client", v.client_name], ["Branch", v.branch_name || "—"],
            ["Mode", v.mode], ["Account", v.account || "—"], ["Reference", v.reference_no || "—"],
            ["Payment Received", formatINR(v.total_amount)], ["TDS", v.tds_total ? formatINR(v.tds_total) : "—"],
            ["CN Allocated", v.cn_allocation_total ? formatINR(v.cn_allocation_total) : "—"],
            ["Adjustments Δ", v.adjustments_net_delta ? formatINR(v.adjustments_net_delta) : "—"],
            ["Retention Held", v.retention_held_total ? formatINR(v.retention_held_total) : "—"],
            ["Retention Released", v.retention_released_total ? formatINR(v.retention_released_total) : "—"],
            ["Total Receipt", formatINR(v.total_receipt ?? v.total_amount)],
            ["Source", v.source === "excel_upload" ? "Excel Upload" : "Manual"],
            ["Created By", v.created_by], ["Created At", (v.created_at || "").slice(0, 16).replace("T", " ")],
            ["Narration", v.narration || "—"]].map(([l, val]) => (
            <div key={l}>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{l}</div>
              <div className="font-semibold text-slate-800 break-words">{val}</div>
            </div>
          ))}
        </div>

        {(v.attachments || []).length > 0 && (
          <div className="px-5 pb-4" data-testid="rvr-attachments">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Attachments</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {(v.attachments || []).map((a, i) => (
                <AttachmentChip key={a.id || i} a={a} testid={`rvr-attachment-${i}`} />
              ))}
            </div>
          </div>
        )}

        {v.status === "Reversed" && (
          <div className="mx-5 mb-4 border border-rose-200 bg-rose-50 p-3 text-xs" data-testid="rvr-reversal-info">
            <div className="font-bold text-rose-800 mb-1">Reversed {(v.reversed_at || "").slice(0, 16).replace("T", " ")} by {v.reversed_by}</div>
            <div className="text-rose-700">Reason: {v.reversal_reason}</div>
            <div className="text-rose-600 mt-1">
              {(v.reversed_receipts || []).length} payment receipt(s) were fully reversed (ledger + invoice received amount).
              {v.retention_reversed && " Retention holds/releases made by this voucher were also restored."}
              {v.adjustments_reversed && " CESS/LD/Round-off adjustments were restored to their previous values."}
              {v.cn_allocations_reversed && " Credit Note allocations were reversed — CN balances and invoice outstanding restored."}
            </div>
          </div>
        )}

        <div className="px-5 pb-4">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
            {v.status === "Reversed" ? "Reversed Payment Receipts" : "Underlying Payment Receipts"}
          </div>
          <table className="w-full text-xs border border-slate-100" data-testid="rvr-receipts-table">
            <thead>
              <tr className="bg-slate-100 text-slate-600">
                {["Receipt No", "Invoice", "Amount", "TDS", "Status"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">{h}</th>))}
              </tr>
            </thead>
            <tbody>
              {(v.status === "Reversed" ? (v.reversed_receipts || []) : (v.receipts || v.allocations || [])).map((r, i) => (
                <tr key={i} className="border-b border-slate-50">
                  <td className="px-3 py-2 font-semibold whitespace-nowrap">{r.payment_number}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.invoice_number || r.against_invoice_number}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatINR(r.amount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {(Number(r.tds_deducted) || 0) + (Number(r.tds_informational) || 0)
                      ? formatINR((Number(r.tds_deducted) || 0) + (Number(r.tds_informational) || 0))
                      : "—"}
                    {Number(r.tds_informational) > 0 && (
                      <span className="ml-1 text-[9px] text-sky-600 font-semibold" title="Recorded from the invoice TDS — already netted in the invoice, not deducted again">rec.</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {v.status === "Reversed"
                      ? <span className="text-rose-600 font-bold">Reversed</span>
                      : r.exists === false
                        ? <span className="text-amber-600 font-bold" title="Deleted directly from Payment Receipts">Deleted externally</span>
                        : <span className="text-emerald-600 font-bold">Posted</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(v.retention_effects || []).length > 0 && (
          <div className="px-5 pb-4" data-testid="rvr-retention-effects">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
              Retention Effects {v.status === "Reversed" ? "(reversed)" : ""}
            </div>
            <table className="w-full text-xs border border-slate-100">
              <thead>
                <tr className="bg-slate-100 text-slate-600">
                  {["Invoice", "Head", "Action", "Amount"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">{h}</th>))}
                </tr>
              </thead>
              <tbody>
                {(v.retention_effects || []).flatMap((e) => [
                  ...(e.holds || []).map((h, i) => (
                    <tr key={`h-${e.invoice_id}-${i}`} className="border-b border-slate-50">
                      <td className="px-3 py-2 whitespace-nowrap">{e.invoice_number}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{h.head}</td>
                      <td className="px-3 py-2"><span className="text-emerald-700 font-bold">Hold</span></td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatINR(h.amount)}</td>
                    </tr>
                  )),
                  ...(e.releases || []).map((r, i) => (
                    <tr key={`r-${e.invoice_id}-${i}`} className="border-b border-slate-50">
                      <td className="px-3 py-2 whitespace-nowrap">{e.invoice_number}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.head}</td>
                      <td className="px-3 py-2"><span className="text-amber-700 font-bold">Release</span></td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatINR(r.amount)}</td>
                    </tr>
                  )),
                ])}
              </tbody>
            </table>
          </div>
        )}

        {(v.cn_allocations || []).length > 0 && (
          <div className="px-5 pb-4" data-testid="rvr-cn-allocations">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
              Credit Note Allocations {v.status === "Reversed" ? "(reversed)" : ""}
            </div>
            {v.batch_cn_context && (
              <div className="text-[10px] text-slate-600 mb-2 flex items-center gap-2 flex-wrap" data-testid="rvr-cn-batch-context">
                <span className="border border-violet-200 bg-violet-50 text-violet-800 px-1.5 py-0.5 font-bold">
                  Batch spread · {v.batch_cn_context.policy_label || v.batch_cn_context.policy}
                </span>
                <span className={`border px-1.5 py-0.5 font-bold ${v.batch_cn_context.mode === "auto" ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                  {v.batch_cn_context.mode === "auto" ? "Auto distribution" : "Manual override"}
                </span>
                <span className="text-slate-500">
                  Batch total {formatINR(v.batch_cn_context.batch_total)} — {(v.batch_cn_context.entries || []).map((e) => `${e.cn_number} ${formatINR(e.amount)}`).join(" · ")}
                </span>
              </div>
            )}
            <table className="w-full text-xs border border-slate-100">
              <thead>
                <tr className="bg-slate-100 text-slate-600">
                  {["Credit Note", "Invoice", "Amount"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">{h}</th>))}
                </tr>
              </thead>
              <tbody>
                {(v.cn_allocations || []).map((c, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="px-3 py-2 font-semibold whitespace-nowrap text-violet-700">{c.cn_number}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{c.invoice_number}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatINR(c.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="text-[10px] text-slate-400 mt-1">CN allocations reduce invoice outstanding directly — no Payment Receipt or bank posting is created.</div>
          </div>
        )}

        {(v.adjustment_effects || []).length > 0 && (
          <div className="px-5 pb-4" data-testid="rvr-adjustment-effects">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
              CESS / LD / Round-off Adjustments {v.status === "Reversed" ? "(reversed)" : ""}
            </div>
            <table className="w-full text-xs border border-slate-100">
              <thead>
                <tr className="bg-slate-100 text-slate-600">
                  {["Invoice", "Field", "Previous", "New", "Delta", "Reason"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">{h}</th>))}
                </tr>
              </thead>
              <tbody>
                {(v.adjustment_effects || []).map((e, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap">{e.invoice_number}</td>
                    <td className="px-3 py-2 font-semibold">{e.label}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatINR(e.previous)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatINR(e.target)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums font-bold ${e.delta >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {e.delta >= 0 ? "−" : "+"}{formatINR(Math.abs(e.delta))} outstanding
                    </td>
                    <td className="px-3 py-2 max-w-[160px] truncate" title={e.reason}>{e.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(v.tds_audit || []).length > 0 && (
          <div className="px-5 pb-4" data-testid="rvr-tds-audit">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">TDS Overrides</div>
            <table className="w-full text-xs border border-slate-100">
              <thead>
                <tr className="bg-slate-100 text-slate-600">
                  {["Invoice", "Suggested", "Actual", "Reason", "By"].map((h) => (
                    <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider">{h}</th>))}
                </tr>
              </thead>
              <tbody>
                {(v.tds_audit || []).map((t, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="px-3 py-2 whitespace-nowrap">{t.invoice_number}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatINR(t.suggested)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold text-amber-700">{formatINR(t.actual)}</td>
                    <td className="px-3 py-2 max-w-[180px] truncate" title={t.reason}>{t.reason}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-500">{t.by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {(v.edit_history || []).length > 0 && (
          <div className="px-5 pb-6">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Edit History</div>
            {(v.edit_history || []).slice().reverse().map((h, i) => (
              <div key={i} className="text-xs border-l-2 border-slate-200 pl-3 py-1.5">
                <span className="font-bold">{h.action === "reallocated" ? "Re-allocated" : "Metadata edited"}</span>
                <span className="text-slate-500"> · {(h.at || "").slice(0, 16).replace("T", " ")} · {h.by}</span>
              </div>
            ))}
          </div>
        )}

        {reverseOpen && <ReverseModal voucher={v} onClose={() => setReverseOpen(false)} onDone={onChanged} />}
      </div>
    </div>
  );
}

function ReverseModal({ voucher, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [working, setWorking] = useState(false);

  async function reverse() {
    if (!reason.trim()) return toast.error("A reversal reason is required");
    setWorking(true);
    try {
      const r = await api.delete(`/receipt-vouchers/${voucher.id}`, { params: { reason: reason.trim() } });
      toast.success(`Voucher reversed — ${r.data.reversed_count} payment receipt(s) rolled back`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reversal failed");
    } finally { setWorking(false); }
  }

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="rvr-reverse-modal" onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md shadow-2xl border border-rose-200 p-5">
        <div className="text-sm font-bold text-rose-800 mb-1">Reverse {voucher.voucher_number}?</div>
        <div className="text-xs text-slate-600 mb-4">
          This fully reverses <span className="font-bold">{voucher.allocation_count} payment receipt(s)</span> —
          ledger postings are cleared and invoice received amounts restored. The voucher remains on the register as a Reversed audit record. This cannot be undone.
        </div>
        <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Reason *</label>
        <textarea data-testid="rvr-reverse-reason" rows={2} className="w-full border border-slate-300 px-3 py-2 text-sm mb-4 focus:outline-none focus:border-rose-500"
          placeholder="Why is this voucher being reversed?" value={reason} onChange={(e) => setReason(e.target.value)} />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button data-testid="rvr-reverse-confirm" onClick={reverse} disabled={working || !reason.trim()}
            className="flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold">
            {working ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Reverse Voucher
          </button>
        </div>
      </div>
    </div>
  );
}
